import { useEffect, useRef } from 'react'
import { isMacLike } from './platformKey'

/** Marks the AI panel's root (side panel and drawer), so its own message box may use the shortcut. */
export const ASSISTANT_PANEL_ATTR = 'data-assistant-panel'

/** The shortcut as tooltips spell it. */
export const ASSISTANT_SHORTCUT_LABEL = 'Ctrl/⌘ + .'

/** The key hint on Ask AI: "⌘." on a Mac, "Ctrl ." elsewhere. */
export function assistantShortcutHint(): string {
  return isMacLike() ? '⌘.' : 'Ctrl .'
}

/** Ctrl + . or ⌘ + . — nothing else held. */
export function isAssistantShortcut(e: KeyboardEvent): boolean {
  if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return false
  return e.key === '.' || e.code === 'Period'
}

const NON_TEXT_INPUTS = new Set(['button', 'checkbox', 'color', 'file', 'image', 'radio', 'range', 'reset', 'submit'])

/** True while the user types somewhere other than the AI panel (a form field, an editor). */
export function isTypingOutsideAssistant(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.closest(`[${ASSISTANT_PANEL_ATTR}]`)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag === 'INPUT') return !NON_TEXT_INPUTS.has((target as HTMLInputElement).type)
  return false
}

/** Ctrl/⌘ + . toggles the AI anywhere, except while typing in another field. */
export function useAssistantShortcut(enabled: boolean, onToggle: () => void) {
  const toggleRef = useRef(onToggle)
  toggleRef.current = onToggle

  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => {
      if (!isAssistantShortcut(e) || isTypingOutsideAssistant(e.target)) return
      e.preventDefault()
      toggleRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled])
}
